import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';

/** 默认闲置超时：24 小时无操作自动退出登录
 *  2026-09-18 用户令"登录过就别再录"：原 30 分钟会日常踢人，
 *  与"同机免重登"直接冲突；24h 保留共用机的安全兜底 */
const DEFAULT_IDLE_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
    'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel',
];

/**
 * 闲置超时自动登出。
 * 已认证状态下监听用户操作事件，超过 timeoutMs 无任何操作则自动登出，
 * App 会随之切换到登录页，并在登录页显示登出原因。
 */
export const useIdleTimeout = (timeoutMs: number = DEFAULT_IDLE_TIMEOUT_MS) => {
    const isAuthenticated = useAuthStore(s => s.isAuthenticated);
    const logout = useAuthStore(s => s.logout);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastResetRef = useRef(0);

    useEffect(() => {
        if (!isAuthenticated) return;

        const onIdle = () => {
            logout('由于长时间未操作，已自动退出登录，请重新登录');
        };

        const resetTimer = () => {
            // 节流：1 秒内的连续事件只重置一次，避免高频事件（mousemove/scroll）反复清表
            const now = Date.now();
            if (now - lastResetRef.current < 1000) return;
            lastResetRef.current = now;

            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(onIdle, timeoutMs);
        };

        resetTimer();
        ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));

        return () => {
            ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, resetTimer));
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [isAuthenticated, timeoutMs, logout]);
};
